import { zodResolver } from '@hookform/resolvers/zod';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Grid,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { z } from 'zod';
import { AccountType, Contact } from '../../../../../../types/api';
import {
  INSTALLER_TYPES,
  INSTALLER_TYPES_LABELS,
  NUMBER_FORMATS,
  PLACEHOLDERS,
} from '../../../constants';
import { useAuth } from '../../../hooks';
import { queryClient } from '../../../queries/queryClient';
import {
  createAccountContactAPI,
  updateAccountContactAPI,
} from '../../../services/apis';
import noticeError from '../../../utils/errors/noticeError';
import { Tracker } from '../../../utils/tracker';
import { NumberFieldWithInfoBox } from '../../hook_form_fields/FieldsWithInfobox';
import { borrowerContactSchema } from '../../dashboard/installers/NewBorrowerForm/schema';
import InputField from '../../hook_form_fields/InputField';
import SelectField from '../../hook_form_fields/SelectField';
import { InfoIcon } from '../../loan_application/icons';
import ResponsibleModal from '../../ResponsibleModal';

type Audience = 'installer' | 'borrower';

type Props = {
  contact?: Contact & { audience?: Audience };
  open: boolean;
  onClose: () => void;
  audience?: Audience;
  applicationId?: string;
  businessName?: string;
};

const schema = z.object({
  first_name: z
    .string()
    .refine((value) => value !== '', "Contact's First name is required"),
  last_name: z
    .string()
    .refine((value) => value !== '', "Contact's Last name is required"),
  email: z
    .string()
    .email('Must be a valid email address')
    .refine((value) => value !== '', "Contact's Email address is required"),
  position_title: z
    .string()
    .refine((value) => value !== '', "Contact's Position/Title is required"),
  phone: z
    .string()
    .nullable()
    .refine(
      (value) => value === null || /^[0-9]+$/.test(value),
      'Please enter numbers only (no letters, spaces, or special characters)'
    )
    .refine(
      (value) => value === null || value.length === 10,
      'Must be 10 digits'
    )
    .refine(
      (value) => value !== null && value !== '',
      'A phone number is required'
    ),
  installer_role: z
    .string()
    .refine((value) => value !== '', 'Role is required'),
});

type FormData = z.infer<typeof schema>;

export default function ContactFormModal({
  open,
  onClose,
  contact,
  audience: createAudience = 'installer',
  applicationId,
  businessName,
}: Props) {
  const isEditing = !!contact;
  const audience = contact?.audience ?? createAudience;
  const isBorrower = audience === 'borrower';
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const tracker = Tracker.getInstance();
  const { user, getUserData } = useAuth();
  const activeSchema = isBorrower
    ? borrowerContactSchema(
        user?.email ?? '',
        isEditing
          ? {
              firstName: contact?.first_name,
              lastName: contact?.last_name,
            }
          : undefined
      )
    : schema;
  const { control, formState, handleSubmit, trigger } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: zodResolver(activeSchema),
    defaultValues: {
      first_name: contact?.first_name ?? '',
      last_name: contact?.last_name ?? '',
      email: contact?.email ?? '',
      position_title: contact?.position_title ?? '',
      phone: contact?.phone ?? '',
      installer_role:
        contact?.installer_role ??
        (user?.account.type === AccountType.CONSULTANT
          ? INSTALLER_TYPES.ADMIN
          : ''),
    },
  });

  useEffect(() => {
    if (contact?.id) trigger();
  }, [contact?.id, trigger]);

  const handleClose = () => {
    if (isLoading) return;

    onClose();
  };

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    try {
      if (isEditing) {
        await updateAccountContactAPI(
          contact.id,
          isBorrower
            ? {
                first_name: data.first_name,
                last_name: data.last_name,
                email: data.email,
                position_title: data.position_title,
                phone: data.phone,
              }
            : data
        );
      } else {
        await createAccountContactAPI(
          isBorrower
            ? {
                first_name: data.first_name,
                last_name: data.last_name,
                email: data.email,
                position_title: data.position_title,
                phone: data.phone,
                audience: 'borrower',
                application_id: applicationId,
              }
            : data
        );
      }
      queryClient.invalidateQueries('contacts');
      queryClient.invalidateQueries('activity');
      queryClient.invalidateQueries('application-access-contacts');
      await getUserData();
      toast.success(
        isEditing ? 'User updated successfully!' : 'User created successfully!'
      );
      tracker.track(
        isEditing ? Tracker.EVENTS.USER_UPDATED : Tracker.EVENTS.USER_CREATED,
        {
          user_id: contact?.id,
          user_first_name: contact?.first_name,
          user_last_name: contact?.last_name,
          user_email: contact?.email,
          user_phone: contact?.phone,
          user_position_title: contact?.position_title,
          user_installer_role: contact?.installer_role,
        }
      );
      onClose();
    } catch (error) {
      console.error(error);
      noticeError(error);
      toast.error(error?.message ?? 'Something went wrong!');
    } finally {
      setIsLoading(false);
    }
  };

  let description = 'Create a new user for the installer portal';
  if (isEditing) {
    description = 'Update user information (email cannot be changed).';
  } else if (isBorrower) {
    description = `Add a user that can sign in and fill out ${
      businessName || 'this borrower'
    }'s application.`;
  }

  return (
    <ResponsibleModal
      open={open}
      onClose={handleClose}
      closeDisabled={isLoading}
      mobileAnchor="bottom"
      title={isEditing ? 'Edit User' : 'Add User'}
      description={description}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container rowSpacing={2.25} columnSpacing={3}>
          <Grid item xs={12} sm={6}>
            <InputField
              fullWidth
              control={control}
              label="First Name"
              name="first_name"
              disabled={isLoading}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <InputField
              fullWidth
              control={control}
              label="Last Name"
              name="last_name"
              disabled={isLoading}
            />
          </Grid>

          <Grid item xs={12}>
            <InputField
              fullWidth
              control={control}
              label="Email Address"
              name="email"
              helperText="Email addresses cannot be changed after user is added. Please verify it is correct."
              disabled={isLoading || isEditing}
            />
          </Grid>

          <Grid item xs={12}>
            <InputField
              fullWidth
              control={control}
              label="Position Title"
              name="position_title"
              disabled={isLoading}
            />
          </Grid>

          <Grid item xs={12}>
            <NumberFieldWithInfoBox
              fullWidth
              control={control}
              label="Phone Number"
              name="phone"
              format={NUMBER_FORMATS.PHONE}
              placeholder={PLACEHOLDERS.PHONE}
              disabled={isLoading}
            />
          </Grid>

          {!isBorrower && (
            <Grid item xs={12}>
              <SelectField
                fullWidth
                control={control}
                label={
                  <Box>
                    <span>Role Type</span>
                    <Tooltip
                      arrow
                      enterTouchDelay={0}
                      title={
                        <Box>
                          <Typography variant="body1" fontWeight={600}>
                            Admin
                          </Typography>
                          <Typography variant="body2">
                            User has full access to the portal, including
                            visibility into all projects and applications. Can
                            price and create new loan applications as well as
                            add and remove users.
                          </Typography>
                          <Typography variant="body1" fontWeight={600} mt={1}>
                            Sales
                          </Typography>
                          <Typography variant="body2">
                            User can price and create new loan applications, but
                            can only view applications they personally
                            submitted. Cannot view approved projects or manage
                            users.
                          </Typography>
                          <Typography variant="body1" fontWeight={600} mt={1}>
                            Operations Manager
                          </Typography>
                          <Typography variant="body1">
                            User can view all approved projects and upload
                            documents to fulfill milestone requirements for
                            funding. Cannot view pending or declined
                            applications or manage users.
                          </Typography>
                        </Box>
                      }
                    >
                      <InfoIcon
                        sx={{
                          fontSize: '14px',
                          mx: '.25rem',
                          verticalAlign: '-.15rem',
                        }}
                      />
                    </Tooltip>
                  </Box>
                }
                name="installer_role"
                choices={[
                  {
                    label: INSTALLER_TYPES_LABELS[INSTALLER_TYPES.ADMIN],
                    value: INSTALLER_TYPES.ADMIN,
                  },
                  {
                    label: INSTALLER_TYPES_LABELS[INSTALLER_TYPES.SALES],
                    value: INSTALLER_TYPES.SALES,
                  },
                  {
                    label: INSTALLER_TYPES_LABELS[INSTALLER_TYPES.ACCOUNTING],
                    value: INSTALLER_TYPES.ACCOUNTING,
                  },
                ]}
                disabled={
                  isLoading || user?.account.type === AccountType.CONSULTANT
                }
              />
            </Grid>
          )}
        </Grid>

        <Box display="flex" justifyContent="flex-end" mt={3} gap={2}>
          <Button
            variant="outlined"
            type="button"
            onClick={handleClose}
            disabled={isLoading}
            fullWidth={isMobile}
          >
            Cancel
          </Button>

          <LoadingButton
            variant="contained"
            type="submit"
            disabled={!formState.isValid || isLoading}
            loading={isLoading}
            fullWidth={isMobile}
          >
            Save
          </LoadingButton>
        </Box>
      </form>
    </ResponsibleModal>
  );
}
